// @ts-ignore
import { GlanceAgent } from './GlanceAgent';
import { Dispatch, SetStateAction } from 'react';

export enum AuthMethod {
  Loginkey = 'LOGINKEY',
  Saml = 'SAML',
  SamlWithLoginkey = 'SAMLLOGINKEY',
}

export interface AuthParams {
  url: string;
  puid: string;
  groupId: string | number;
  method?: AuthMethod | string;
  headers?: Object;
  body?: URLSearchParams;
  debug?: boolean;
  errorHandler?: (message: string, hideError?: boolean) => void;
}

interface LoginKey {
  key: string;
  expiration: number;
}

interface JoinParams {
  cobrowseKey: string;
  openLocation?: string;
  debug?: boolean;
  sessionStateHandler?: (activeSession: boolean) => void;
}
export interface PresenceParams {
  presenceCallback: Dispatch<SetStateAction<boolean>>;
  permissionsCallback: () => void;
  visitorId: string;
  debug?: boolean;
  endSessionCallback?: (data) => void;
  startSessionCallback?: (data) => void;
}

interface EventListenerParams {
  endSessionCallback?: (data) => void;
  debug?: boolean;
}
enum ErrorEvents {
  AuthenticateFailed = 'authenticateFailed',
  PresenceFailed = 'presenceFailed',
  PresenceError = 'presenceError',
  PresenceInvokeFailed = 'presenceInvokeFailed',
  LookupFailed = 'lookupFailed',
}

enum PresenceOnTermsStatus {
  Accepted = 'accepted',
  Declined = 'declined',
  Displayed = 'displayed',
}

export enum ErrorUserMessages {
  LoginKeyError = 'There are issues with authentication, please refresh the page',
  ConnectPresenceError = 'There are issues with connecting to the presence service, please try again or refresh the page',
  PresenceAuthorizationError = 'Partner User Id "{0}" not found or invalid, contact your administrator',
  PresenceDeclinedError = 'The website visitor declined cobrowse session',
  LookupSessionNotFoundError = 'Session not found. Check that the key is correct',
  LookupSessionError = 'There are issues connecting cobrowse session, please try again or refresh the page',
  JoinSessionError = 'There are issues with the visitor joining the cobrowse session, please try again or refresh the page',
  InviteSessionError = 'There are issues with inviting the visitor to the mobile camera share session, please try again or refresh the page',
  InviteSessionBusinessError = '{0}, please try again or refresh the page',
  EndPresenceError = 'There are issues with ending the cobrowse session, please try again or refresh the page',
  EndCobrowseError = 'There are issues with ending the cobrowse session, please try again or refresh the page',
}

export class AgentService {
  protected Agent = GlanceAgent;
  protected loginKeyParams: LoginKey;
  protected auth: AuthParams;
  protected sessionInfo: any;
  protected scheduleLoginKeyRenewRef: number = 0;
  protected scheduleEndSessionCheck: number = 0;
  private openedWindow;

  constructor(auth: AuthParams) {
    this.auth = auth;
  }

  authWithLoginkey = async (auth: AuthParams, regenerate = false) => {
    this.auth = auth;
    const loginKey = await this.getLoginkey(auth);
    this.loginKeyParams = loginKey;

    this.Agent.authenticate({
      partnerid: auth.groupId,
      partneruserid: auth.puid,
      loginkey: this.loginKeyParams.key,
    });

    this.scheduleLoginKeyRenew(regenerate);
  };

  private defaultLogger(method: string, message: string) {
    const loggerPrefix = `Agent.service.${method}`;
    if (this.auth.debug) {
      const loggerMessage = `DEBUG: ${loggerPrefix}: ${message}`;
      console.log(loggerMessage);
    }
  }

  defaultErrorHandler(
    error: Error,
    context?: string,
    userMessage?: string,
    hideError?: boolean
  ) {
    const errorPrefix = 'Agent.service';
    if (!error) {
      error = new Error(context ?? 'NoErrorContext');
    }
    const errorMessage = error.message ?? error.name;
    if (this.auth.debug) {
      const debugErrorMessage = `ERROR: ${errorPrefix} ${context ?? errorMessage
        }`;
      console.error(debugErrorMessage, error ?? '');
    }
    if (this.auth.errorHandler) {
      const handlerMessage = userMessage ?? `${context ?? ''}${errorMessage}`;
      this.auth.errorHandler(handlerMessage, hideError);
    }
  }

  getLoginkey = async (auth: AuthParams): Promise<LoginKey> => {
    const loginKeyUrl = new URL(auth.url);
    // NOTE: lowercased param to preserve backward-compatibility with the existing and defined NPM properties and behavior
    const partnerUserIdKey = 'partnerUserId'.toLowerCase();
    loginKeyUrl.searchParams.set(partnerUserIdKey, auth.puid);
    let headers = {};

    if (auth.headers) {
      headers = auth.headers;
    }

    let body = null;

    if (auth.body) {
      body = auth.body;
    }

    try {
      const response = await fetch(loginKeyUrl, {
        body: body as BodyInit,
        method: 'POST',
        headers,
      });

      const { loginKey, expiration } = await response.json();
      return { key: loginKey, expiration };
    } catch (error) {
      this.defaultErrorHandler(
        error,
        'getLoginkey',
        ErrorUserMessages.LoginKeyError
      );
    }
  };

  public get loginKey() {
    return this.loginKeyParams.key;
  }

  public get puid() {
    return this.auth.puid;
  }

  public get groupId() {
    return this.auth.groupId;
  }

  protected scheduleLoginKeyRenew = (regenerate = false) => {
    const methodName = 'scheduleLoginKeyRenew';
    const secToMilliseconds = 1000;
    this.defaultLogger(
      methodName,
      `current expiration - ${this.loginKeyParams.expiration}, at ${new Date(
        this.loginKeyParams.expiration * secToMilliseconds
      )}`
    );
    // NOTE: if we've regenerate passed, we cancel previous schedule and add a new one
    if (regenerate && this.scheduleLoginKeyRenewRef > 0) {
      clearTimeout(this.scheduleLoginKeyRenewRef);
      this.scheduleLoginKeyRenewRef = 0;
    }
    // NOTE: we need to get renewed Login before `secondsToRenewBeforeExpire` the expiration
    const secondsToRenewBeforeExpire = 30;
    const renewTimestamp =
      this.loginKeyParams.expiration - secondsToRenewBeforeExpire;
    // NOTE: it's safe to rely on local Date, since we're working with unix-timestamps and converting result to milliseconds
    const delayMilliSeconds =
      renewTimestamp * secToMilliseconds - new Date().getTime();
    if (delayMilliSeconds > 0 && !(this.scheduleLoginKeyRenewRef > 0)) {
      this.scheduleLoginKeyRenewRef = setTimeout(
        (async () => {
          this.defaultLogger(
            methodName,
            `renew id - ${this.scheduleLoginKeyRenewRef
            }, invoked at ${new Date()}`
          );
          this.scheduleLoginKeyRenewRef = 0;
          await this.authWithLoginkey(this.auth);
        }).bind(this),
        delayMilliSeconds
      );
      this.defaultLogger(
        methodName,
        `renew is scheduled with id - ${this.scheduleLoginKeyRenewRef
        }, at ${new Date(renewTimestamp * secToMilliseconds)}.`
      );
    } else {
      if (this.scheduleLoginKeyRenewRef > 0) {
        this.defaultLogger(
          methodName,
          `method invoke, renew already scheduled with id - ${this.scheduleLoginKeyRenewRef
          }, at ${new Date(renewTimestamp * secToMilliseconds)}`
        );
      } else {
        const message = `LoginKey expiration ${this.loginKeyParams.expiration} is in the past or less than ${secondsToRenewBeforeExpire} sec from now, check your loginKey generation logic`;
        throw new Error(message);
      }
    }
    return this.scheduleLoginKeyRenewRef;
  };

  handleEvents = () => {
    const addErrorHandler = (
      errorEventType: ErrorEvents,
      errorUserMessage: ErrorUserMessages,
      errorCustomHandler?: (event) => void
    ) => {
      this.Agent.addEventListener(errorEventType, async (event) => {
        const errorWrapper = new Error(JSON.stringify(event));
        errorWrapper.name = errorEventType;
        if (errorCustomHandler) {
          await errorCustomHandler(event);
        }
        else {
          this.defaultErrorHandler(
            errorWrapper,
            errorEventType,
            errorUserMessage
          );
        }
      });
    };

    const reAuthenticateHandler = async (event) => {
      if (
        this.auth.method == AuthMethod.Loginkey &&
        event.reason.startsWith(
          'Authenticate failed with status LOGIN_FAILED.'
        ) &&
        event.reason.includes('Login key expired:')
      ) {
        await this.authWithLoginkey(this.auth, true);
        // NOTE: we've the ability to hide errors, but better to leave that to users
        // if(this.loginKey.expiration > new Date().getTime()/1000) {
        //   this.defaultErrorHandler(null, null, null, true);
        // }
        this.defaultLogger(
          'reAuthenticateHandler',
          `Re-authenticate invoked due to: ${event.reason}`
        );
      }
    };

    addErrorHandler(
      ErrorEvents.AuthenticateFailed,
      ErrorUserMessages.LoginKeyError
    );
    addErrorHandler(
      ErrorEvents.PresenceFailed,
      ErrorUserMessages.ConnectPresenceError,
      this.presenceFailedAuthorizationHandler
    );
    addErrorHandler(
      ErrorEvents.PresenceInvokeFailed,
      ErrorUserMessages.ConnectPresenceError
    );
    addErrorHandler(
      ErrorEvents.PresenceError,
      ErrorUserMessages.ConnectPresenceError
    );
    addErrorHandler(
      ErrorEvents.LookupFailed,
      ErrorUserMessages.LookupSessionError,
      reAuthenticateHandler
    );
  };

  presenceFailedAuthorizationHandler = async (payload): Promise<boolean> => {
    const authorizationMarker = 'authorization';
    let returnValue = false;
    // NOTE: in exception handling the only `authorizationMarker` string is available, in event - json-structure with reason field   
    // Overall, it's a design issues with BA API where there is no strict division between 'error' events and error throws
    if (
      payload === authorizationMarker ||
      payload.reason === authorizationMarker
    ) {
      const userMessage = ErrorUserMessages.PresenceAuthorizationError.replace(
        '{0}',
        this.auth.puid
      );
      this.defaultLogger('presenceFailedAuthorizationHandler', userMessage);
      const errorWrapper = new Error(payload);
      errorWrapper.name = ErrorEvents.PresenceFailed;
      this.defaultErrorHandler(
        errorWrapper,
        ErrorEvents.PresenceFailed,
        userMessage
      );
      returnValue = true;
    }
    return returnValue;
  };

  authWithSaml = (idpid: string) => {
    this.Agent.authenticate({ idpid: idpid });
  };

  authWithSamlLoginKey = (idpid: string) => {
    const methodName = 'authWithSamlLoginKey';
    try {
      // NOTE: see - https://stackoverflow.com/questions/5639346/what-is-the-shortest-function-for-reading-a-cookie-by-name-in-javascript
      const getCookieValue = (name) =>
        document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)')?.pop() ||
        '';
      // NOTE: we need cookie to be different per group (puid?) to avoid intersection in some edge-case scenarios
      const authCookieName = `GLANCE_SamlLoginKey_${idpid}`.toUpperCase();
      const authCookieValueRaw = getCookieValue(authCookieName);
      // NOTE: here is the structure that is expected for SAMLWithLoginKey method - authCookieValueRaw can be uncommented to emulate cookie-based authenticate
      // const authCookieValueRaw = encodeURIComponent(JSON.stringify({
      //   partnerid: <>,
      //   partneruserid: <>,
      //   loginkey: <>,
      // }));
      const authCookieValue = JSON.parse(
        decodeURIComponent(authCookieValueRaw)
      );
      const loginKeyValue = authCookieValue['loginkey'];
      const getExpiration = (loginKey: string) => {
        const expirationStartPosition = 2;
        const expirationStartSymbol = '$';
        return parseInt(
          loginKey.substring(
            expirationStartPosition + 1,
            loginKey.indexOf(expirationStartSymbol, expirationStartPosition + 1)
          )
        );
      };
      this.loginKeyParams = {
        key: loginKeyValue,
        expiration: getExpiration(loginKeyValue),
      };
      this.auth.groupId = authCookieValue['partnerid'];
      this.auth.puid = authCookieValue['partneruserid'];
      this.defaultLogger(
        methodName,
        `${authCookieName} - ${JSON.stringify(authCookieValue)}`
      );
      this.Agent.authenticate(authCookieValue);
    } catch (error) {
      this.defaultErrorHandler(
        error,
        methodName,
        ErrorUserMessages.LoginKeyError
      );
    }
  };

  setAgentListeners = (eventParams: EventListenerParams) => {
    this.Agent.addEventListener('sessionEnded', () => {
      if (eventParams.endSessionCallback) eventParams.endSessionCallback(false);
      if (eventParams.debug) console.log('Glance sessionEnded');
    });
  };

  connectPresence = async (presence: PresenceParams) => {
    try {
        await this.Agent.presenceConnect(presence.visitorId);
    } catch (error) {
      console.log(`presenceConnect error: ${error}.`);
      // NOTE: here we're exlcuding general handling of errors that are custom/concrete and are supposed to be handled with listening events
      const isCustomHandledError = await this.presenceFailedAuthorizationHandler(error);
      if (!isCustomHandledError) {
        this.defaultErrorHandler(
          error,
          'connectPresence',
          ErrorUserMessages.ConnectPresenceError
        );
      }
    }
  };

  disconnectPresence = async () => {
    this.Agent.presenceDisconnect();
  };

  cobrowseService = (join: JoinParams) => {
    try {
      this.Agent.joinSession(join.cobrowseKey);
    } catch (error) {
      this.defaultErrorHandler(
        error,
        'cobrowseService',
        ErrorUserMessages.JoinSessionError
      );
    }
  };

  cancelEndSessionCheck = () => {
    if (this.scheduleEndSessionCheck) {
      clearInterval(this.scheduleEndSessionCheck);
    }
    this.scheduleEndSessionCheck = 0;
  };

  lookupService = async (join: JoinParams): Promise<string> => {
    try {
      this.sessionInfo = await this.Agent.lookupSession(join.cobrowseKey);
      const { agentjoinurl } = this.sessionInfo;

      // NOTE: we check each 5 sec for an active cobrowse session existence
      const scheduleEndSessionCheckSec = 5;
      this.cancelEndSessionCheck();
      this.scheduleEndSessionCheck = setInterval(async () => {
        const result = await this.checkSession(join.cobrowseKey);
        if (join.sessionStateHandler) join.sessionStateHandler(result);
        this.defaultLogger(
          `lookupService`,
          `scheduleEndSessionCheck: ${result}`
        );
      }, scheduleEndSessionCheckSec * 1e3);

      return agentjoinurl;
    } catch (error) {
      // NOTE: commenting until we've the event-based flow
      // this.defaultErrorHandler(
      //   error,
      //   'lookupService',
      //   ErrorUserMessages.LookupSessionError
      // );
      this.defaultLogger(`lookupService`, `Error - ${error}`);
    }
  };

  lookupServiceCancel = () => {
    this.Agent.cancelLookup();
  };

  presenceStartSession = async (
    sessionKey: string,
    acceptHandler: () => void,
    declineHandler: () => void
  ): Promise<void> => {
    const presenceOnTermsEventName = 'presenceOnTerms';
    const methodName = 'presenceStartSession';
    const showTermsEventHandler = (eventData) => {
      switch (eventData.status) {
        case PresenceOnTermsStatus.Accepted:
          this.defaultLogger(methodName, PresenceOnTermsStatus.Accepted);
          acceptHandler();
          break;
        case PresenceOnTermsStatus.Declined:
          this.defaultLogger(methodName, PresenceOnTermsStatus.Declined);
          declineHandler();
          break;
        default:
      }
      this.defaultLogger(
        methodName,
        `Event ${presenceOnTermsEventName} handled, data: ${eventData}`
      );
    };
    this.Agent.addEventListener(
      presenceOnTermsEventName,
      showTermsEventHandler
    );
    this.Agent.presenceStartSession(sessionKey);
  };

  presenceDisconnect = async (): Promise<void> => {
    this.Agent.presenceDisconnect();
  };

  setPresenceVisitorId = (visitorId: string) => {
    this.Agent.setPresenceVisitorId(visitorId);
  };

  registerEventsHandler = (
    handler: (eventName: string, eventData: any) => void
  ) => {
    this.Agent.addEventListenerAll(handler);
  };

  // TODO: here is workaround with using authUrl as backendUrl, overall we need to revise the NPM properties / WebApp usage of them
  getBackendUrl = (path) => {
    const backendUrl = this.auth.url.replace('/loginKey/', path);
    return backendUrl;
  };

  checkSession = async (sessionCode: string) => {
    return await this.Agent.inSession(sessionCode);
  };

  presenceEndSession = async (visitorId: string) => {
    const presenseEndBackendUrl = this.getBackendUrl('/presence/end/');
    const visitorIdKey = 'visitorId';
    const partnerUserIdKey = 'partnerUserId';
    const presenceSessionEndUrl = new URL(presenseEndBackendUrl);
    presenceSessionEndUrl.searchParams.set(partnerUserIdKey, this.auth.puid);
    presenceSessionEndUrl.searchParams.set(visitorIdKey, visitorId);

    const context = 'presenceEndSession';
    try {
      let headers = {};
      if (this.auth.headers) headers = this.auth.headers;
      const presenceSessionEndRequest = await fetch(presenceSessionEndUrl, {
        method: 'POST',
        headers,
      });
      if (!presenceSessionEndRequest?.ok) {
        const errorMessage = `Status code: ${presenceSessionEndRequest.status}`;
        this.defaultErrorHandler(
          new Error(errorMessage),
          context,
          ErrorUserMessages.EndPresenceError
        );
      }
    } catch (e) {
      const errorMessage = `Exception: ${e.message}`;
      this.defaultErrorHandler(
        new Error(errorMessage, { cause: e }),
        context,
        ErrorUserMessages.EndPresenceError
      );
    }
    this.cancelEndSessionCheck();
  };

  cobrowseEndSession = async (sessionCode: string) => {
    const cobrowseEndBackendUrl = this.getBackendUrl('/cobrowse/end/');
    const sessionCodeKey = 'sessionCode';
    const partnerUserIdKey = 'partnerUserId';
    const cobrowseSessionEndUrl = new URL(cobrowseEndBackendUrl);
    cobrowseSessionEndUrl.searchParams.set(partnerUserIdKey, this.auth.puid);
    cobrowseSessionEndUrl.searchParams.set(sessionCodeKey, sessionCode);

    const context = 'cobrowseEndSession';
    try {
      let headers = {};
      if (this.auth.headers) headers = this.auth.headers;
      const cobrowseSessionEndRequest = await fetch(cobrowseSessionEndUrl, {
        method: 'POST',
        headers,
      });
      if (!cobrowseSessionEndRequest?.ok) {
        const errorMessage = `Status code: ${cobrowseSessionEndRequest.status}`;
        this.defaultErrorHandler(
          new Error(errorMessage),
          context,
          ErrorUserMessages.EndCobrowseError
        );
      }
    } catch (e) {
      const errorMessage = `Exception: ${e.message}`;
      this.defaultErrorHandler(
        new Error(errorMessage, { cause: e }),
        context,
        ErrorUserMessages.EndCobrowseError
      );
    }
    this.cancelEndSessionCheck();
  };

  hasAgentWindow = () => {
    const returnValue = this.openedWindow && !this.openedWindow.closed;
    return returnValue;
  };

  closeAgentWindow = () => {
    let returnValue = false;
    if (this.hasAgentWindow()) {
      this.openedWindow.close();
      returnValue = true;
    }
    return returnValue;
  };

  openWindow = (
    url: string,
    params: string,
    closeHandler: (reason: any) => void
  ) => {
    const agentWindowCloseReason = { reason: 'agent' };
    const windowCloseIntervalCheckSec = 1;
    // TODO: should be changed to the Browser Agent API - openAgentJoinURL method when it's fixed to support tabs
    this.openedWindow = window.open(url, params);
    // NOTE: cross-domain window close check - see https://stackoverflow.com/questions/9388380/capture-the-close-event-of-popup-window-in-javascript
    let openedWindowTimer = setInterval(() => {
      if (this.openedWindow.closed) {
        closeHandler(agentWindowCloseReason);
        clearInterval(openedWindowTimer);
        this.defaultLogger('openWindow', `openedWindowTimer check is handled`);
      }
    }, windowCloseIntervalCheckSec * 1e3);
  };

  checkPermissions = (
    permissions: {
      name: string;
      expectedValue: any;
      handler: (hasPermission: boolean) => void;
    }[]
  ) => {
    const context = 'checkPermissions';
    const authorizationTokenClaims = this.getPresenceJwtTokenClaims();
    for (const permission of permissions) {
      const hasPermission =
        authorizationTokenClaims[permission.name] === permission.expectedValue;
      permission.handler(hasPermission);
      this.defaultLogger(
        context,
        `permission: ${permission.name}=${permission.expectedValue}, result: ${hasPermission}`
      );
    }
  };

  getPresenceJwtTokenClaims() {
    let returnValue = {};
    const context = 'getPresenceJwtTokenClaims';
    try {
      if (
        window.GLANCE &&
        window.GLANCE.Authorization &&
        window.GLANCE.Authorization.authorized()
      ) {
        // NOTE: `gettoken` method is not marked with export, but it's accessible
        const authorizationToken = window.GLANCE.Authorization.gettoken();
        // NOTE: we can use jsonwebtoken npm to decode JWT claims, but we don't actually need that additional dependency
        const authorizationTokenJson = JSON.parse(
          atob(authorizationToken.split('.')[1])
        );
        returnValue = authorizationTokenJson;
      }
    } catch (e) {
      const errorMessage = `Exception: ${e.message}`;
      this.defaultErrorHandler(
        new Error(errorMessage, { cause: e }),
        context,
        ErrorUserMessages.ConnectPresenceError
      );
    }
    return returnValue;
  }
}
